import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, CheckCircle2, XCircle, ChevronRight, 
  BookOpen, Award, Lightbulb, RotateCcw, Volume2, VolumeX
} from 'lucide-react';
import axios from 'axios';
import { getCabinLessonContent as getStaticCabinLessonContent } from '../data/cabinCrewContents';
import { getCabinModule as getStaticCabinModule } from '../data/cabinCrewCurriculum';
import { fetchLessonContent } from '../services/curriculumService';
import { DragDropGame, MatchingGame, TimeRaceGame, TrueFalseGame, FillInBlankGame } from '../components/CabinGames';
import { useSoundEffects } from '../utils/soundEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinLesson() {
  const { lessonId, moduleId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const sounds = useSoundEffects();
  
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [gameScore, setGameScore] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hasPlayedStartSound, setHasPlayedStartSound] = useState(false);

  useEffect(() => {
    // Yeni ders yüklendiğinde tüm state'leri sıfırla
    setCompleted(false);
    setStep(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setScore(0);
    setQuizAnswers([]);
    setGameScore(0);
    setHasPlayedStartSound(false);
    setLoadError(null);
    
    loadLessonContent();
  }, [lessonId, moduleId]);

  const loadLessonContent = async () => {
    console.log('Loading lesson content for:', lessonId, 'moduleId:', moduleId);
    setLoadError(null);
    
    try {
      // Önce API'den dene
      if (moduleId) {
        const apiContent = await fetchLessonContent(moduleId, '1', lessonId);
        if (apiContent) {
          console.log('API content loaded:', apiContent);
          setContent(apiContent);
          return;
        }
      }
    } catch (error) {
      console.log('API error, falling back to static:', error);
    }
    
    // Fallback: Static dosyadan yükle
    const lessonContent = getStaticCabinLessonContent(lessonId);
    console.log('Static content loaded:', lessonContent ? 'Found' : 'NOT FOUND', lessonContent?.type);
    
    if (!lessonContent) {
      console.error('Lesson content not found for:', lessonId);
      setLoadError(`Ders içeriği bulunamadı: ${lessonId}`);
    }
    
    setContent(lessonContent);
  };

  // Ses efekti ayrı useEffect'te - sadece bir kez çalsın
  useEffect(() => {
    if (content && !hasPlayedStartSound && soundEnabled) {
      sounds.playStart();
      setHasPlayedStartSound(true);
    }
  }, [content, hasPlayedStartSound, soundEnabled, sounds]);

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    sounds.setEnabled(newState);
  };

  const handleGameComplete = (result) => {
    const finalScore = typeof result === 'number' ? result : result ? 100 : 0;
    setGameScore(finalScore);
    setCompleted(true);
    
    // Başarı sesi
    if (soundEnabled) {
      if (finalScore >= 70) {
        sounds.playSuccess();
      } else {
        sounds.playWarning();
      }
    }
  };

  const handleAnswer = (option) => {
    if (showFeedback) return;
    
    setSelectedAnswer(option);
    const correct = option.isCorrect;
    setIsCorrect(correct);
    setShowFeedback(true);
    
    // Ses efekti çal
    if (soundEnabled) {
      if (correct) {
        sounds.playCorrect();
      } else {
        sounds.playWrong();
      }
    }
    
    if (correct) {
      setScore(prev => prev + (content.scenario?.score || 10));
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...quizAnswers];
    newAnswers[questionIndex] = answerIndex;
    setQuizAnswers(newAnswers);
    
    // Tıklama sesi
    if (soundEnabled) {
      sounds.playClick();
    }
  };

  const handleNext = () => {
    // Interactive, lesson ve scenario tipleri için dersi tamamla
    if (content.type === 'interactive' || content.type === 'lesson' || content.type === 'scenario') {
      setCompleted(true);
      if (soundEnabled) sounds.playSuccess();
    } else {
      // Diğer tipler için sadece feedback'i kapat
      setShowFeedback(false);
      setSelectedAnswer(null);
    }
  };

  const handleQuizSubmit = () => {
    // Quiz veya Exam için çalış
    const questions = content.quiz?.questions || content.exam?.questions;
    if (!questions) return;
    
    let correctCount = 0;
    questions.forEach((q, i) => {
      if (quizAnswers[i] === q.correct) {
        correctCount++;
      }
    });
    
    const percentage = Math.round((correctCount / questions.length) * 100);
    setScore(percentage);
    setCompleted(true);
    
    // Sınav sonucu sesi
    if (soundEnabled) {
      if (percentage >= 70) {
        sounds.playSuccess();
      } else {
        sounds.playWarning();
      }
    }
  };

  // Bir sonraki dersi bul
  const getNextLesson = () => {
    // lessonId kontrolü
    if (!lessonId || typeof lessonId !== 'string') {
      console.error('Invalid lessonId:', lessonId);
      return null;
    }
    
    // lessonId formatı: CM-M1-U1-L1
    const parts = lessonId.split('-');
    if (parts.length < 4) {
      console.error('Invalid lessonId format:', lessonId);
      return null;
    }
    
    const moduleNum = parts[1].replace('M', '');
    const unitNum = parts[2].replace('U', '');
    const lessonNum = parseInt(parts[3].replace('L', ''));
    
    const module = getStaticCabinModule(moduleNum);
    if (!module || !module.units) {
      console.error('Module not found:', moduleNum);
      return null;
    }
    
    // Mevcut üniteyi bul
    const currentUnitIndex = module.units.findIndex(u => u.unit_id === `CM-M${moduleNum}-U${unitNum}`);
    if (currentUnitIndex === -1) {
      console.error('Unit not found:', `CM-M${moduleNum}-U${unitNum}`);
      return null;
    }
    
    const currentUnit = module.units[currentUnitIndex];
    
    // Aynı ünitede bir sonraki ders var mı?
    const currentLessonIndex = currentUnit.lessons.findIndex(l => l.lesson_id === lessonId);
    if (currentLessonIndex < currentUnit.lessons.length - 1) {
      // Aynı ünitede sonraki ders var
      return currentUnit.lessons[currentLessonIndex + 1];
    }
    
    // Sonraki ünite var mı?
    if (currentUnitIndex < module.units.length - 1) {
      const nextUnit = module.units[currentUnitIndex + 1];
      if (nextUnit.lessons && nextUnit.lessons.length > 0) {
        return { ...nextUnit.lessons[0], newUnit: true, unitTitle: nextUnit.title };
      }
    }
    
    // Modül tamamlandı
    return { moduleComplete: true, moduleId: moduleNum };
  };

  const handleComplete = async () => {
    try {
      // lessonId kontrolü
      if (!lessonId || typeof lessonId !== 'string') {
        console.error('handleComplete: Invalid lessonId', lessonId);
        navigate('/cabin-training');
        return;
      }
      
      // lessonId formatı: "CM-M1-U1-L1" veya "cabin-m1-u1-l1"
      const parts = lessonId.replace('cabin-', '').split('-');
      const moduleId = parts[1]?.replace('M', '') || parts[0] || '1';  // M1 -> 1
      const unitId = parts[2] || '';  // U1
      
      await axios.post(`${API}/cabin-training/complete-lesson`, {
        lesson_id: lessonId,
        module_id: moduleId,
        unit_id: unitId,
        score: score,
        miles: content?.xp || 40
      }, { withCredentials: true });
      
      // Sonraki dersi bul
      const nextLesson = getNextLesson();
      
      // State'leri sıfırla
      setCompleted(false);
      setStep(0);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setScore(0);
      setQuizAnswers([]);
      setGameScore(0);
      
      if (nextLesson?.moduleComplete) {
        // Modül tamamlandı, modül sayfasına dön
        navigate(`/cabin-training/module/${nextLesson.moduleId}`);
      } else if (nextLesson?.lesson_id) {
        // Sonraki derse git
        navigate(`/cabin-training/lesson/${nextLesson.lesson_id}`);
      } else {
        // Hata durumunda modül sayfasına dön
        if (lessonId && typeof lessonId === 'string') {
          const parts = lessonId.split('-');
          const moduleId = parts.length > 1 ? parts[1].replace('M', '') : '1';
          navigate(`/cabin-training/module/${moduleId}`);
        } else {
          navigate('/cabin-training');
        }
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
      // Hata durumunda da modül sayfasına dön
      if (lessonId && typeof lessonId === 'string') {
        const parts = lessonId.split('-');
        const moduleId = parts.length > 1 ? parts[1].replace('M', '') : '1';
        navigate(`/cabin-training/module/${moduleId}`);
      } else {
        navigate('/cabin-training');
      }
    }
  };

  if (!content) {
    // Hata varsa göster
    if (loadError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-4">❌</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Ders Bulunamadı</h2>
              <p className="text-gray-600 mb-4">{loadError}</p>
              <Button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700">
                <ArrowLeft className="w-4 h-4 mr-2" /> Geri Dön
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">📚</div>
          <p>Ders yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Completed Screen
  if (completed) {
    const nextLesson = getNextLesson();
    const isModuleComplete = nextLesson?.moduleComplete;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-500 to-teal-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Tebrikler! 🎉</h2>
            <p className="text-gray-600 mb-4">{content.title} tamamlandı</p>
            
            <div className="flex justify-center gap-4 mb-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-600">+{content.xp}</p>
                <p className="text-sm text-gray-500">Mil Kazanıldı</p>
              </div>
              {content.type !== 'lesson' && (
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{score}%</p>
                  <p className="text-sm text-gray-500">Başarı</p>
                </div>
              )}
            </div>
            
            {/* Sonraki Ders Bilgisi */}
            {nextLesson && !isModuleComplete && (
              <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-1">
                  {nextLesson.newUnit ? `📚 Yeni Ünite: ${nextLesson.unitTitle}` : 'Sonraki Ders'}
                </p>
                <p className="text-sm font-bold text-blue-800">{nextLesson.title}</p>
              </div>
            )}
            
            {isModuleComplete && (
              <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-sm font-bold text-amber-800">🎓 Modül Tamamlandı!</p>
                <p className="text-xs text-amber-600">Harika iş çıkardın!</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Button onClick={handleComplete} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {isModuleComplete ? 'Modüle Dön' : 'Sonraki Derse Geç'} <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
              
              {!isModuleComplete && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (lessonId && typeof lessonId === 'string') {
                      const parts = lessonId.split('-');
                      const moduleId = parts.length > 1 ? parts[1].replace('M', '') : '1';
                      navigate(`/cabin-training/module/${moduleId}`);
                    } else {
                      navigate('/cabin-training');
                    }
                  }} 
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Üniteye Dön
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Interactive Content - Quiz içeren kısa dersler
  if (content.type === 'interactive') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10 shadow-sm">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleSound} 
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title={soundEnabled ? 'Sesi Kapat' : 'Sesi Aç'}
              >
                {soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="flex items-center gap-1 text-emerald-600">
                <Award className="w-4 h-4" />
                <span className="text-sm font-bold">+{content.xp}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {/* Title & Intro */}
          <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-5">
              <h2 className="text-xl font-bold text-indigo-800 mb-2">{content.content?.title}</h2>
              <p className="text-gray-700">{content.content?.intro}</p>
            </CardContent>
          </Card>

          {/* Facts/Categories/Steps/Timeline */}
          {content.content?.facts && (
            <div className="grid grid-cols-2 gap-3">
              {content.content.facts.map((fact, i) => (
                <Card key={i} className="hover:shadow-md transition">
                  <CardContent className="p-4 text-center">
                    <span className="text-2xl mb-2 block">{fact.icon}</span>
                    <h3 className="font-bold text-sm text-gray-800">{fact.title}</h3>
                    <p className="text-xs text-gray-600 mt-1">{fact.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {content.content?.categories && (
            <div className="space-y-3">
              {content.content.categories.map((cat, i) => (
                <Card key={i} className="hover:shadow-md transition">
                  <CardContent className="p-4 flex items-center gap-4">
                    <span className="text-3xl">{cat.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-800">{cat.title}</h3>
                      <p className="text-sm text-indigo-600">{cat.examples}</p>
                      <p className="text-xs text-gray-500">{cat.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {content.content?.steps && (
            <div className="space-y-3">
              {content.content.steps.map((step, i) => (
                <Card key={i} className="hover:shadow-md transition border-l-4 border-indigo-400">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-700">
                      {step.level}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{step.title}</h3>
                      <p className="text-sm text-gray-500">{step.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {content.content?.timeline && (
            <div className="space-y-2">
              {content.content.timeline.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-16 text-center">
                    <span className="font-mono font-bold text-indigo-600">{item.time}</span>
                  </div>
                  <span className="text-xl">{item.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-800">{item.activity}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {content.content?.benefits && (
            <div className="space-y-3">
              {content.content.benefits.map((b, i) => (
                <Card key={i} className="hover:shadow-md transition">
                  <CardContent className="p-4 flex items-center gap-4">
                    <span className="text-2xl">{b.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-800">{b.title}</h3>
                      <p className="text-sm text-gray-600">{b.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {content.content?.challenges && (
            <div className="space-y-3">
              {content.content.challenges.map((c, i) => (
                <Card key={i} className="hover:shadow-md transition border-l-4 border-orange-400">
                  <CardContent className="p-4 flex items-center gap-4">
                    <span className="text-2xl">{c.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-800">{c.title}</h3>
                      <p className="text-sm text-gray-600">{c.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {content.content?.summary && (
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
              <CardContent className="p-4">
                <div className="space-y-2">
                  {content.content.summary.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mini Challenge */}
          {content.content?.challenge && !showFeedback && (
            <Card className="border-2 border-amber-300 bg-amber-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🎯</span>
                  <h3 className="font-bold text-amber-800">Mini Soru</h3>
                </div>
                <p className="font-medium text-gray-800 mb-4">{content.content.challenge.question}</p>
                <div className="space-y-3">
                  {content.content.challenge.options.map((opt, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      onClick={() => {
                        setSelectedAnswer(opt);
                        setIsCorrect(opt.isCorrect);
                        setShowFeedback(true);
                        if (soundEnabled) {
                          opt.isCorrect ? sounds.playCorrect() : sounds.playWrong();
                        }
                      }}
                      className="w-full p-4 h-auto text-left justify-start bg-white border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50 transition-all"
                    >
                      {opt.text}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Feedback */}
          {showFeedback && (
            <Card className={`border-2 ${isCorrect ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  {isCorrect ? (
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <span className="font-bold text-lg">{isCorrect ? 'Doğru!' : 'Yanlış!'}</span>
                </div>
                <p className="text-gray-700">{selectedAnswer?.feedback}</p>
                <Button 
                  onClick={handleNext} 
                  className={`w-full mt-4 ${isCorrect ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  Devam Et <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Lesson Content (Reading)
  if (content.type === 'lesson' || content.type === 'review') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleSound} 
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title={soundEnabled ? 'Sesi Kapat' : 'Sesi Aç'}
              >
                {soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="flex items-center gap-1 text-emerald-600">
                <Award className="w-4 h-4" />
                <span className="text-sm font-bold">+{content.xp}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">{content.content?.title}</h2>
              </div>
              
              <div className="space-y-4">
                {content.content?.paragraphs?.map((p, i) => (
                  <p key={i} className="text-gray-700 leading-relaxed">{p}</p>
                ))}
              </div>

              {/* Key Points */}
              {content.content?.keyPoints && (
                <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    <span className="font-bold text-amber-800">Önemli Noktalar</span>
                  </div>
                  <ul className="space-y-2">
                    {content.content.keyPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-amber-900">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Button 
                onClick={handleNext} 
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
              >
                Dersi Tamamla <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Scenario Content
  if (content.type === 'scenario') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">Senaryo</span>
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleSound} 
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title={soundEnabled ? 'Sesi Kapat' : 'Sesi Aç'}
              >
                {soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="flex items-center gap-1 text-emerald-600">
                <Award className="w-4 h-4" />
                <span className="text-sm font-bold">+{content.xp}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              {/* Context */}
              {content.scenario?.context && (
                <div className="p-4 bg-blue-50 rounded-xl mb-4">
                  <p className="text-blue-800 text-sm">{content.scenario.context}</p>
                </div>
              )}
              
              {/* Question */}
              <h2 className="text-xl font-bold text-gray-800 mb-6">{content.scenario?.question}</h2>

              {/* Options */}
              <div className="space-y-3">
                {content.scenario?.options?.map((option) => {
                  const isSelected = selectedAnswer?.id === option.id;
                  const showResult = showFeedback && isSelected;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleAnswer(option)}
                      disabled={showFeedback}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        showResult
                          ? option.isCorrect
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : showFeedback && option.isCorrect
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          showResult
                            ? option.isCorrect
                              ? 'bg-green-500 text-white'
                              : 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {option.id}
                        </span>
                        <span className="flex-1 font-medium">{option.text}</span>
                        {showResult && (
                          option.isCorrect 
                            ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                            : <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Feedback */}
              {showFeedback && (
                <div className={`mt-6 p-4 rounded-xl ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`font-bold mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                    {isCorrect ? '✅ Doğru!' : '❌ Yanlış'}
                  </p>
                  <p className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                    {isCorrect ? content.scenario?.feedback?.correct : content.scenario?.feedback?.wrong}
                  </p>
                  {content.scenario?.explanation && (
                    <p className="mt-2 text-gray-600 text-sm border-t pt-2 mt-2">
                      <strong>Açıklama:</strong> {content.scenario.explanation}
                    </p>
                  )}
                </div>
              )}

              {showFeedback && (
                <Button 
                  onClick={handleNext} 
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Devam Et <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Content
  if (content.type === 'quiz' || content.type === 'exam') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.type === 'exam' ? 'Sınav' : 'Quiz'}</span>
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleSound} 
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title={soundEnabled ? 'Sesi Kapat' : 'Sesi Aç'}
              >
                {soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <VolumeX className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="flex items-center gap-1 text-emerald-600">
                <Award className="w-4 h-4" />
                <span className="text-sm font-bold">+{content.xp}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {(content.quiz?.questions || content.exam?.questions)?.map((q, qIndex) => (
            <Card key={qIndex}>
              <CardContent className="p-4">
                <p className="font-bold text-gray-800 mb-3">
                  {qIndex + 1}. {q.q}
                </p>
                <div className="space-y-2">
                  {q.options.map((opt, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleQuizAnswer(qIndex, oIndex)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        quizAnswers[qIndex] === oIndex
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          <Button 
            onClick={handleQuizSubmit}
            disabled={(() => {
              const questions = content.quiz?.questions || content.exam?.questions || [];
              const answeredCount = quizAnswers.filter(a => a !== undefined && a !== null).length;
              return answeredCount < questions.length;
            })()}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            Sınavı Bitir
          </Button>
        </div>
      </div>
    );
  }

  // ==================== DRAG & DROP GAME ====================
  if (content.type === 'dragdrop') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-1 text-purple-600">
              <Award className="w-4 h-4" />
              <span className="text-sm font-bold">+{content.xp}</span>
            </div>
          </div>
        </div>

        {/* Game */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              {content.dragdrop?.instruction && (
                <p className="text-gray-600 mb-4 text-center">{content.dragdrop.instruction}</p>
              )}
              <DragDropGame 
                items={content.dragdrop?.items || []}
                onComplete={(success) => {
                  if (success) {
                    setScore(100);
                    setCompleted(true);
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==================== MATCHING GAME ====================
  if (content.type === 'matching') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-1 text-blue-600">
              <Award className="w-4 h-4" />
              <span className="text-sm font-bold">+{content.xp}</span>
            </div>
          </div>
        </div>

        {/* Game */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              <MatchingGame 
                pairs={content.matching?.pairs || []}
                onComplete={(success) => {
                  if (success) {
                    setScore(100);
                    setCompleted(true);
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==================== TIME RACE GAME ====================
  if (content.type === 'timerace') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-1 text-yellow-600">
              <Award className="w-4 h-4" />
              <span className="text-sm font-bold">+{content.xp}</span>
            </div>
          </div>
        </div>

        {/* Game */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              <TimeRaceGame 
                questions={content.timerace?.questions || []}
                timePerQuestion={content.timerace?.timePerQuestion || 8}
                onComplete={(finalScore) => {
                  setScore(finalScore);
                  setCompleted(true);
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==================== TRUE/FALSE GAME ====================
  if (content.type === 'truefalse') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-1 text-green-600">
              <Award className="w-4 h-4" />
              <span className="text-sm font-bold">+{content.xp}</span>
            </div>
          </div>
        </div>

        {/* Game */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              <TrueFalseGame 
                statements={content.truefalse?.statements || []}
                onComplete={(percentage) => {
                  setScore(percentage);
                  setCompleted(true);
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==================== FILL IN BLANK GAME ====================
  if (content.type === 'fillin') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-800">{content.title}</span>
            <div className="flex items-center gap-1 text-indigo-600">
              <Award className="w-4 h-4" />
              <span className="text-sm font-bold">+{content.xp}</span>
            </div>
          </div>
        </div>

        {/* Game */}
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-6">
              <FillInBlankGame 
                sentences={content.fillin?.sentences || []}
                onComplete={(percentage) => {
                  setScore(percentage);
                  setCompleted(true);
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Bilinmeyen içerik tipi - hata göster
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">İçerik Yüklenemedi</h2>
          <p className="text-gray-600 mb-4">
            Bu ders içeriği ({content?.type || 'bilinmeyen tip'}) henüz desteklenmiyor veya yüklenemedi.
          </p>
          <p className="text-xs text-gray-400 mb-4">Ders ID: {lessonId}</p>
          <Button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Geri Dön
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
